import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    // Check if user is already logged in
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setIsLoading(false);
          return;
        }
        
        const response = await authService.getCurrentUser();
        const userData = response.user; // Backend'den { success: true, user: {...} } formatında geliyor
        setCurrentUser(userData);
        setIsAuthenticated(true);

        // Set user permissions
        if (userData.permissions) {
          setPermissions(userData.permissions);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  const login = async (credentials) => {
    try {
      const { user, token } = await authService.login(credentials);
      
      localStorage.setItem('token', token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      
      // Set user permissions
      if (user.permissions) {
        setPermissions(user.permissions);
      }
      
      toast.success('Giriş başarılı!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Giriş başarısız!');
      return false;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
      setPermissions({});
      toast.info('Çıkış yapıldı');
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authService.forgotPassword(email);
      toast.success('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Şifre sıfırlama işlemi başarısız!');
      return false;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await authService.resetPassword(token, newPassword);
      toast.success('Şifreniz başarıyla sıfırlandı. Giriş yapabilirsiniz.');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Şifre sıfırlama işlemi başarısız!');
      return false;
    }
  };

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (currentUser?.role === 'admin') return true;
    return permissions[permission] === true;
  };

  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    permissions,
    login,
    logout,
    forgotPassword,
    resetPassword,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
